<template>
    <ModalDialogContainer ref="modalContainer" v-model:display="displayStatus">
        <div class="modal-dialog modal-lg" role="document" @click="stopPropagationEvent">
            <div class="modal-header">
                <div class="modal-title">{{ name ? name + " - " + $t("Data space participant") : $t("Data space participant") }}</div>
                <button type="button" class="modal-close-btn" :title="$t('Close')" @click="close">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div class="modal-body no-padding">
                <div class="table-responsive">
                    <table class="table">
                        <tr>
                            <td class="bold">{{ $t("ID") }}</td>
                            <td class="bold">{{ participantId || "-" }}</td>

                            <td></td>
                        </tr>
                        <tr>
                            <td class="bold">{{ $t("Name") }}</td>

                            <td v-if="loading"><i class="fa fa-spinner fa-spin"></i> {{ $t("Loading") }}...</td>
                            <td v-else class="bold">{{ name || "-" }}</td>

                            <td></td>
                        </tr>
                        <tr>
                            <td class="bold">{{ $t("Connector URL") }}</td>

                            <td v-if="loading"><i class="fa fa-spinner fa-spin"></i> {{ $t("Loading") }}...</td>
                            <td v-else>{{ url }}</td>

                            <td></td>
                        </tr>

                        <tr v-for="(a, i) in attributes" :key="i">
                            <td class="bold">{{ $t("Attribute") }}: {{ a.name }}</td>
                            <td>{{ a.value }}</td>

                            <td class="text-right on-line">
                                <button
                                    v-if="a.attestation >= 0"
                                    type="button"
                                    class="btn btn-xs btn-primary"
                                    @click="viewAttestation(a.attestation)"
                                    :title="$t('Attestation')"
                                >
                                    <i class="fas fa-certificate no-margin"></i>
                                </button>
                            </td>
                        </tr>
                    </table>
                </div>
            </div>
            <div class="modal-footer">
                <button v-if="allowFilter" type="button" class="btn btn-primary btn-mr" @click="filterBy" :disabled="busyUpdating">
                    <i class="fas fa-filter"></i> {{ $t("Filter") }}
                </button>
                <button
                    v-if="canUpdate && url"
                    type="button"
                    class="btn btn-primary btn-mr"
                    @click="updateParticipant"
                    :disabled="busyUpdating"
                >
                    <i v-if="!busyUpdating" class="fas fa-sync-alt"></i>
                    <i v-else class="fa fa-spinner fa-spin"></i>
                    {{ $t("Update") }}
                </button>
                <button type="button" class="btn btn-primary btn-mr" @click="close" :disabled="busyUpdating">
                    <i class="fas fa-check"></i> {{ $t("Done") }}
                </button>
            </div>
        </div>

        <AttestationViewModal
            v-if="displayAttestationModal"
            v-model:display="displayAttestationModal"
            :attestation="selectedAttestation"
            :subject="participantId"
            :subject-name="name"
        ></AttestationViewModal>
    </ModalDialogContainer>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useVModel } from "../../utils/v-model";
import { getUniqueStringId } from "@/utils/unique-id";
import { Timeouts } from "@/utils/timeout";
import { ApiDataSpaceParticipants } from "@/api/api-group-data-space-participants";
import { DataSpaceKnownParticipantAttestation, DataSpaceKnownParticipantAttribute } from "@/api/definitions";
import { Request } from "@asanrom/request-browser";
import { AuthController } from "@/control/auth";
import AttestationViewModal from "./AttestationViewModal.vue";

type DataSpaceKnownParticipantAttributeExtended = DataSpaceKnownParticipantAttribute & {
    attestation: number;
};

function mapAttributes(
    attributes: DataSpaceKnownParticipantAttribute[],
    attestations: DataSpaceKnownParticipantAttestation[],
): DataSpaceKnownParticipantAttributeExtended[] {
    const attMap = new Map<string, number>();
    attestations.forEach((a, i) => {
        a.attributes.forEach((atr) => {
            attMap.set(atr.name, i);
        });
    });

    return attributes.map((a) => {
        let attestation = -1;

        if (attMap.has(a.name)) {
            attestation = attMap.get(a.name);
        }

        return {
            name: a.name,
            value: a.value,
            attestation: attestation,
        };
    });
}

export default defineComponent({
    components: {
        AttestationViewModal,
    },
    name: "DataSpaceParticipantModal",
    emits: ["update:display", "filter-by"],
    props: {
        display: Boolean,
        participantId: String,
        allowFilter: Boolean,
    },
    setup(props) {
        return {
            loadRequestId: getUniqueStringId(),
            displayStatus: useVModel(props, "display"),
            updateRequestId: getUniqueStringId(),
        };
    },
    data: function () {
        return {
            loading: true,

            name: "",
            url: "",

            canUpdate: AuthController.hasPermission("participants.admin"),
            busyUpdating: false,

            attributes: [] as DataSpaceKnownParticipantAttributeExtended[],
            attestations: [] as DataSpaceKnownParticipantAttestation[],

            selectedAttestation: null as DataSpaceKnownParticipantAttestation,
            displayAttestationModal: false,
        };
    },
    methods: {
        load: function () {
            Timeouts.Abort(this.loadRequestId);

            this.loading = true;
            this.attributes = [];

            Request.Pending(this.loadRequestId, ApiDataSpaceParticipants.GetKnownParticipant(this.participantId))
                .onSuccess((res) => {
                    this.loading = false;

                    this.name = res.name;
                    this.url = res.url;
                    this.attributes = mapAttributes(res.attributes, res.attestations);
                    this.attestations = res.attestations;
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        notFound: () => {
                            this.name = "";
                            this.url = "";
                            this.attributes = [];
                            this.loading = false;
                        },
                        temporalError: () => {
                            Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                });
        },

        updateParticipant: function () {
            if (this.busyUpdating) {
                return;
            }

            this.busyUpdating = true;

            Request.Pending(
                this.updateRequestId,
                ApiDataSpaceParticipants.AddKnownParticipant({
                    url: this.url,
                }),
            )
                .onSuccess((res) => {
                    this.busyUpdating = false;

                    this.name = res.name;
                    this.url = res.url;
                    this.attributes = mapAttributes(res.attributes, res.attestations);
                    this.attestations = res.attestations;

                    this.$showSnackBar(this.$t("Participant details have been updated!"));
                })
                .onRequestError((err, handleErr) => {
                    this.busyUpdating = false;
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        badRequest: () => {
                            this.$showSnackBar(this.$t("Error") + ": " + this.$t("Bad request"));
                        },
                        notFound: () => {
                            this.$showSnackBar(this.$t("Error") + ": " + this.$t("Could not connect to the remote participant"));
                        },
                        forbidden: () => {
                            this.$showSnackBar(this.$t("Error") + ": " + this.$t("Access denied"));
                        },
                        serverError: () => {
                            this.$showSnackBar(this.$t("Error") + ": " + this.$t("Internal server error"));
                        },
                        networkError: () => {
                            this.$showSnackBar(this.$t("Error") + ": " + this.$t("Could not connect to the server"));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    this.busyUpdating = false;
                    this.$showSnackBar(this.$t("Error") + ": " + err.message);
                });
        },

        filterBy: function () {
            this.$emit("filter-by");
            this.close();
        },

        close: function () {
            this.$closeModal();
        },

        escapeToClose: function (event) {
            if (event.key === "Escape") {
                this.close();
            }
        },

        stopPropagationEvent: function (e) {
            e.stopPropagation();
        },

        viewAttestation: function (i: number) {
            const att = this.attestations[i];

            if (!att) {
                return;
            }

            this.selectedAttestation = att;
            this.displayAttestationModal = true;
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-changed", () => {
            this.canUpdate = AuthController.hasPermission("participants.admin");
        });

        if (this.display) {
            this.$autoFocus();
            this.load();
        }
    },
    beforeUnmount: function () {
        Timeouts.Abort(this.loadRequestId);
        Request.Abort(this.loadRequestId);
        Request.Abort(this.updateRequestId);
    },
    watch: {
        display: function () {
            if (this.display) {
                this.$autoFocus();
                this.load();
            }
        },
    },
});
</script>

<style></style>
